import { Alert } from '@risksmart-app/atomic-ui';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import type { FormContextProps } from 'src/components/form/form/types';
import { useOrgScopedLocalStorage } from 'src/hooks/useOrgScopedLocalStorage';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import ObligationDetailsFormFields from './ObligationDetailsFormFields';
import type { ObligationFormFieldData } from './obligationSchema';
import { ObligationSchema } from './obligationSchema';

export type Props = Omit<
  FormContextProps<ObligationFormFieldData>,
  'formId' | 'header' | 'i18n' | 'parentType' | 'renderTemplate' | 'schema'
> & {
  obligationId?: string;
  parentObligationNode?: {
    Id: string;
    SequentialId?: null | number | undefined;
    ObjectType: ParentType;
  } | null;
  latestTestDate?: null | string;
  initialType?: ObligationFormFieldData['Type'];
  /**
   * Pre-selects the parent on the parent-scoped create route
   * (`/compliance/obligation/:obligationId/add`). Mirrors `RiskForm`'s
   * `initialParentRiskId`.
   */
  initialParentId?: null | string;
  /**
   * Locks the Type radio and the Parent select together (COI-642). Both are
   * fixed by the surface that linked here — the dashboard column supplies the
   * type, the route supplies the parent — and they cannot be locked
   * independently: the Type radio's onChange nulls `ParentId`, so an unlocked
   * type with a locked parent would strand the form with no selectable parent.
   */
  typeAndParentLocked?: boolean;
  external?: boolean;
};

const ObligationDetailsForm: FC<Props> = ({ defaultValues, ...props }) => {
  const { t } = useTranslation(['common']);
  const { user } = useRisksmartUser();
  const {
    hasPermission: canInsertStandardObligations,
    loading: canInsertStandardObligationsLoading,
  } = useHasPermissionQuery('insert:obligation');
  const defaultType =
    props.initialType ||
    ((canInsertStandardObligations && !canInsertStandardObligationsLoading) ||
    props.obligationId
      ? 'standard'
      : 'chapter');
  const defaultData: ObligationFormFieldData = {
    ...(defaultValues as ObligationFormFieldData),
    Type: defaultType,
    ...(props.initialParentId ? { ParentId: props.initialParentId } : {}),
    Owners: [
      {
        type: 'user',
        value: user!.userId,
      },
    ],
  };

  const [alertDismissed, setAlertDismissed] = useOrgScopedLocalStorage(false, {
    localStorageKey: 'ExternalObligationAlertDismissed',
  });
  const handleDismiss = useCallback(() => {
    setAlertDismissed(true);
  }, [setAlertDismissed]);

  return (
    <CustomisableForm
      {...props}
      defaultValues={defaultData}
      formId={'obligation-form'}
      i18n={t('obligations')}
      header={t('details')}
      schema={ObligationSchema}
      parentType={Parent_Type_Enum.Obligation}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    >
      {props.external && !alertDismissed && (
        <Alert variant={'active'}>
          <Alert.Header>
            <Alert.Title>
              {t('obligations.external_obligation.alert_title')}
            </Alert.Title>
            <Alert.Dismiss onClick={handleDismiss} />
          </Alert.Header>
          <Alert.Description className={'text-base'}>
            {t('obligations.external_obligation.alert_message')}
          </Alert.Description>
        </Alert>
      )}
      <ObligationDetailsFormFields
        readOnly={props.readOnly}
        obligationId={props.obligationId}
        external={props.external}
        parentObligationNode={props.parentObligationNode}
        latestTestDate={props.latestTestDate}
        typeAndParentLocked={props.typeAndParentLocked}
      />
    </CustomisableForm>
  );
};

export default ObligationDetailsForm;
